import { type FormEvent, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../auth/AuthProvider";
import { Logo, Spinner } from "../components/Icons";
import { ApiError } from "../lib/api";

export default function Login() {
  const { admin, ready, signIn } = useAuth();
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (ready && admin) navigate("/admin", { replace: true });
  }, [ready, admin, navigate]);

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await signIn(username, password);
      navigate("/admin", { replace: true });
    } catch (err) {
      setError(
        err instanceof ApiError ? err.message : "Could not reach the server. Is it running?",
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <main
      style={{
        minHeight: "100vh",
        display: "grid",
        placeItems: "center",
        padding: 24,
        background:
          "radial-gradient(900px 500px at 85% -10%, rgba(95,168,189,0.18), transparent 60%)," +
          "radial-gradient(700px 480px at -10% 100%, rgba(51,112,140,0.12), transparent 60%)," +
          "var(--bg)",
      }}
    >
      <div style={{ width: "100%", maxWidth: 400 }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            justifyContent: "center",
            marginBottom: 28,
          }}
        >
          <Logo size={34} />
          <span style={{ fontSize: 28, fontWeight: 700, letterSpacing: "-0.5px" }}>Ripple</span>
        </div>

        <form
          className="card"
          onSubmit={onSubmit}
          style={{ animation: "cardIn 300ms var(--ease-default)" }}
        >
          <h1 style={{ fontSize: 20, marginBottom: 4 }}>Sign in</h1>
          <p className="muted" style={{ marginBottom: 22 }}>
            Live audience polling. Build a deck, present it, watch answers land.
          </p>

          <label className="label" htmlFor="username">
            Username
          </label>
          <input
            id="username"
            className="input"
            autoComplete="username"
            autoFocus
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            style={{ marginBottom: 16 }}
          />

          <label className="label" htmlFor="password">
            Password
          </label>
          <input
            id="password"
            className="input"
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            style={{ marginBottom: 20 }}
          />

          {error && (
            <p
              role="alert"
              style={{
                marginBottom: 16,
                padding: "10px 14px",
                borderRadius: "var(--radius-control)",
                background: "#F8EAE7",
                color: "var(--error)",
                fontSize: 14,
              }}
            >
              {error}
            </p>
          )}

          <button className="btn" style={{ width: "100%" }} disabled={busy || !username}>
            {busy ? <Spinner /> : "Sign in"}
          </button>
        </form>

        <p className="tiny" style={{ textAlign: "center", marginTop: 20 }}>
          Participants don't sign in — they join with a code.
        </p>
      </div>
    </main>
  );
}
